/** @jsxImportSource @emotion/react */
import { useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router';

import { type ApiPropertyProject, type ApiUpdatePropertyProject, getPropertyActivityLogQueryKeyPrefix } from '@kaiizen/kai-pro-api';
import { useAuth, useOrgMarkets } from '@kaiizen/kai-pro-api-react-state';
import { useQueryCache } from '@kaiizen/react-persistent-cache';

import { PageLayout } from '@/Client/Layout';
import { usePageTitle } from '@/Client/Lib';
import { NewJobModal, PropertyActivitySection, PropertyJobsSection, PropertyOverviewHead } from '@/Client/Partials/PropertyOverview';
import { useAutoSwitchOrgForResource, useOrgMarket, useOrgProperty, useProjectTypes, usePropertyProjects } from '@/Client/State';
import { useNavigateToMarkets, useNavigateToProject } from '@/Client/State/kaiNavigation';
import { GenericLoadingPage } from '../../GenericPages';
import { DEFAULT_ORG_PROPERTY_PAGE_NEW_STYLES, type OrgPropertyPageNewStyles } from './orgPropertyPageNewStyles';

/**
 * The new experience's property page (`/property/:id` for users opted into it): the property and
 * its facts, its jobs (projects) and what has happened to them. Jobs are created in a modal.
 */
export const OrgPropertyPageNew = ({ styles = DEFAULT_ORG_PROPERTY_PAGE_NEW_STYLES }: { styles?: OrgPropertyPageNewStyles }) => {
  const { id } = useParams();
  const { userCan } = useAuth();
  const { orgProperty, propertyMutators, isError, error } = useOrgProperty(id);
  const { orgMarket } = useOrgMarket(orgProperty?.orgMarketId ?? undefined);
  const { markets } = useOrgMarkets();
  const { getProjectTypeLabel } = useProjectTypes();
  // Every job, archived ones included: the table splits them, and the header and activity read all of them.
  const { propertyProjects, propertyProjectMutators } = usePropertyProjects({ orgPropertyId: id, includeArchived: true });
  const queryCache = useQueryCache();
  const navigateToProject = useNavigateToProject();
  const navigateToMarkets = useNavigateToMarkets();
  const [showNewJob, setShowNewJob] = useState(false);
  const jobsRef = useRef<HTMLElement>(null);
  usePageTitle(orgProperty?.address ? `Property · ${orgProperty.address}` : undefined);
  useAutoSwitchOrgForResource({ error: isError ? error : undefined, orgPropertyId: id });

  if (!id) return <Navigate to='/properties' replace />;
  if (!orgProperty || !propertyProjects) return <GenericLoadingPage />;

  const propertyIsArchived = !!orgProperty.archivedAt;
  const openJob = (project: ApiPropertyProject) => navigateToProject({ projectId: project.id });
  const updateJob = async (projectId: string, update: ApiUpdatePropertyProject) => {
    await propertyProjectMutators.update(projectId, update);
    // The change is now in the property's activity.
    await queryCache.refetchAll({ prefix: getPropertyActivityLogQueryKeyPrefix(orgProperty.id) });
  };

  return (
    <PageLayout
      title={`Property: ${orgProperty.address || ''}${orgMarket ? ` (Market: ${orgMarket.name})` : ''}${propertyIsArchived ? ' — Archived' : ''}`}
      contentLayoutStyling={'standard'}
    >
      <div css={styles.root}>
        <PropertyOverviewHead
          orgProperty={orgProperty}
          orgMarket={orgMarket}
          markets={markets ?? []}
          projects={propertyProjects}
          canEdit={userCan('update', 'OrgProperty')}
          saveProperty={update => propertyMutators.update(orgProperty.id, update)}
          onViewMarket={orgMarket ? () => navigateToMarkets({ id: orgMarket.id }) : undefined}
          onShowJobs={() => jobsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        />
        <PropertyJobsSection
          ref={jobsRef}
          projects={propertyProjects}
          propertyIsArchived={propertyIsArchived}
          getProjectTypeLabel={getProjectTypeLabel}
          canCreate={userCan('create', 'PropertyProject')}
          canUpdate={userCan('update', 'PropertyProject')}
          onNewJob={() => setShowNewJob(true)}
          onOpenJob={openJob}
          updateJob={updateJob}
        />
        <PropertyActivitySection
          orgPropertyId={orgProperty.id}
          projects={propertyProjects}
          getProjectTypeLabel={getProjectTypeLabel}
          onOpenJob={openJob}
        />
      </div>
      {/* Mounted only while open, so the page doesn't load templates and market users nobody asked for. */}
      {showNewJob
        ? (
          <NewJobModal
            visible
            onClose={() => setShowNewJob(false)}
            property={orgProperty}
            projectMutators={propertyProjectMutators}
          />
        )
        : null}
    </PageLayout>
  );
};
